import { useState, useRef, useEffect, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, CheckIn } from '../lib/supabase';
import { useEmberStore } from '../lib/store';
import { toast } from '../lib/toast';
import { Star, Martini, Flame, Edit, Trash2, Navigation } from 'lucide-react';

interface CheckInCardProps {
  checkIn: CheckIn;
  onDelete?: () => void;
  enablePhotoFocus?: boolean;
}

type CardViewState = 'collapsed' | 'expanded' | 'photo';

export default function CheckInCard({ checkIn, onDelete, enablePhotoFocus = false }: CheckInCardProps) {
  const navigate = useNavigate();
  const { currentUser } = useEmberStore();
  const [viewState, setViewState] = useState<CardViewState>('collapsed');
  const cardRef = useRef<HTMLDivElement | null>(null);
  const notesRef = useRef<HTMLDivElement | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { 
    tobacco, 
    cellar_tobacco,
    cellar_pipe,
    cellar_lighter,
    tobacco_manual,
    pipe_manual,
    lighter_manual,
    rating, 
    drink_pairing, 
    weather, 
    pipe_shape_used, 
    pipe_make,
    outdoor_seating,
    covered_seating,
    heaters,
    photo_url, 
    venue,
    created_at 
  } = checkIn;

  // Format date
  const formattedDate = new Date(created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  // Build amenities string
  const amenities = [];
  if (outdoor_seating) amenities.push('🌳 Outdoor');
  if (covered_seating) amenities.push('☂️ Covered');
  if (heaters) amenities.push('🔥 Heaters');
  
  // Resolve photo URL - treat fallback as valid photo when enablePhotoFocus is true
  const resolvedPhotoUrl = photo_url || (enablePhotoFocus ? '/ember-bg.jpg' : null);
  const hasPhoto = !!resolvedPhotoUrl;
  const backgroundImageValue = resolvedPhotoUrl ? `url(${resolvedPhotoUrl})` : null;

  // Check if user owns this check-in
  const isOwner = currentUser?.id === checkIn.user_id;

  // Border color based on ownership
  const borderColor = isOwner ? 'var(--color-secondary)' : 'var(--color-success)';

  // Check if there's expandable content
  const hasCellarImages = cellar_tobacco?.image_url || cellar_pipe?.image_url;
  const hasNotes = !!checkIn.notes;
  const hasUsernameForOthers = !isOwner && !!(checkIn as any).profile?.username;
  const hasExpandableContent = hasCellarImages || hasNotes || hasUsernameForOthers || isOwner;

  async function handleDelete() {
    // Create styled confirmation modal
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 1rem;
    `;
    
    const content = document.createElement('div');
    content.style.cssText = `
      background: #1a1a1a;
      border: 1px solid #e4574d;
      border-radius: 12px;
      padding: 2rem;
      max-width: 400px;
      width: 100%;
      text-align: center;
      color: #ffffff;
    `;
    
    content.innerHTML = `
      <h3 style="font-size: 1.25rem; margin-bottom: 1rem; color: #e4574d;">Delete Check-in</h3>
      <p style="color: #cfcfcf; margin-bottom: 1.5rem;">Are you sure you want to delete this check-in? This action cannot be undone.</p>
      <div style="display: flex; gap: 1rem; justify-content: center;">
        <button id="cancel-delete" style="
          padding: 0.75rem 1.5rem;
          background: transparent;
          color: #999;
          border: 1px solid #666;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
        ">Cancel</button>
        <button id="confirm-delete" style="
          padding: 0.75rem 1.5rem;
          background: #e4574d;
          color: #ffffff;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
        ">Delete</button>
      </div>
    `;
    
    modal.appendChild(content);
    document.body.appendChild(modal);
    
    // Handle button clicks
    const cancelBtn = content.querySelector('#cancel-delete');
    const confirmBtn = content.querySelector('#confirm-delete');
    
    cancelBtn?.addEventListener('click', () => modal.remove());
    
    confirmBtn?.addEventListener('click', async () => {
      modal.remove();
      
      setDeleting(true);
      try {
        const { error } = await supabase
          .from('checkins')
          .delete()
          .eq('id', checkIn.id);

        if (error) throw error;
        
        if (onDelete) onDelete();
      } catch (err) {
        console.error('Error deleting check-in:', err);
        toast.error('Failed to delete check-in');
      } finally {
        setDeleting(false);
      }
    });
  }

  const allowPhotoFocus = enablePhotoFocus && hasPhoto;
  const isCollapsed = viewState === 'collapsed';
  const isExpandedDetails = viewState === 'expanded';
  const isPhotoOnly = viewState === 'photo';
  const isCardOpen = viewState !== 'collapsed';

  // Check if notes section is scrollable
  useEffect(() => {
    if (notesRef.current && isExpandedDetails) {
      const notesEl = notesRef.current;
      const isScrollable = notesEl.scrollHeight > notesEl.clientHeight;
      if (isScrollable) {
        notesEl.classList.add('scrollable');
      } else {
        notesEl.classList.remove('scrollable');
      }
    }
  }, [isExpandedDetails, checkIn.notes]);

  const handleCardToggle = () => {
    if (!hasExpandableContent) {
      return;
    }

    setViewState((prev) => {
      if (!allowPhotoFocus) {
        return prev === 'collapsed' ? 'expanded' : 'collapsed';
      }

      if (prev === 'collapsed') {
        return 'expanded';
      }
      if (prev === 'expanded') {
        return 'photo';
      }
      return 'collapsed';
    });
  };

  // Build container class names
  const containerClasses = [
    'checkin-card-container',
    isCollapsed ? 'collapsed' : isExpandedDetails ? 'expanded' : 'photo'
  ].filter(Boolean).join(' ');

  return (
    <div 
      id={`checkin-${checkIn.id}`}
      className={containerClasses}
      ref={cardRef}
      onClick={hasExpandableContent ? handleCardToggle : undefined}
      style={{
        cursor: hasExpandableContent ? 'pointer' : 'default',
        border: `1px solid ${borderColor}`,
        touchAction: 'manipulation', // Optimize touch handling on mobile
        WebkitTapHighlightColor: 'transparent', // Remove iOS tap highlight
      }}
    >
      {/* Background layer */}
      {backgroundImageValue && (
        <div 
          className="checkin-card-background"
          style={{
            backgroundImage: backgroundImageValue,
          }}
        />
      )}

      {/* Gradient overlay - hidden in photo-only state */}
      {backgroundImageValue && <div className="checkin-card-overlay" />}

      {/* Fallback flame icon for cards without user-uploaded photos */}
      {!photo_url && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            opacity: 0.2,
          }}
        >
          <Flame size={220} color="#0b0b0b" strokeWidth={1.1} />
        </div>
      )}

      {/* Expand pill indicator */}
      {hasExpandableContent && !isPhotoOnly && !isExpandedDetails && (
        <div
          className={`expand-pill ${isOwner ? 'pill-owner' : 'pill-guest'}`}
          aria-hidden="true"
        />
      )}

      {/* Main Card Content */}
          <div className="checkin-card-content">
        <div className="checkin-card-details">
          {/* Main Content - always visible */}
          <div style={{ flex: '0 0 auto' }}>
            {/* Tobacco Name - Priority: cellar > global > manual */}
            <h3 style={{ fontSize: '1.125rem', marginBottom: '0.25rem', fontWeight: '600', color: '#f5f5f5' }}>
              {cellar_tobacco ? `${cellar_tobacco.brand} - ${cellar_tobacco.blend_name}` : tobacco?.name || tobacco_manual || 'Unknown Tobacco'}
            </h3>

            {/* Rating row directly under tobacco in collapsed state */}
            {isCollapsed && (
              <div style={{ display: 'flex', gap: '0.2rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                {Array.from({ length: 10 }).map((_, i) => (
                  <Star
                    key={i}
                    size={14}
                    fill={i < ((rating <= 5 ? rating * 2 : rating) || 0) ? '#ff9500' : 'none'}
                    color={i < ((rating <= 5 ? rating * 2 : rating) || 0) ? '#ff9500' : '#6b4423'}
                  />
                ))}
              </div>
            )}

            {/* Additional details only in expanded/detail states */}
            {!isCollapsed && (
              <>
                {/* Tobacco Details */}
                <p style={{ fontSize: '0.875rem', color: '#999', marginBottom: '0.75rem' }}>
                  {cellar_tobacco ? (
                    <>
                      {cellar_tobacco.amount}{cellar_tobacco.unit === 'grams' ? 'g' : 'oz'}
                      {cellar_tobacco.storage_type && ` • ${
                        cellar_tobacco.storage_type === 'tin'
                          ? '🥫'
                          : cellar_tobacco.storage_type === 'jar'
                          ? '🫙'
                          : cellar_tobacco.storage_type === 'pouch'
                          ? '👝'
                          : '📦'
                      }`}
                      {' • '}
                <span style={{ 
                  color: cellar_tobacco.status === 'sealed' ? 'var(--color-success)' : cellar_tobacco.status === 'opened' ? '#facc15' : '#999',
                        fontWeight: '500'
                      }}>
                        {cellar_tobacco.status === 'sealed' && '🟢 Sealed'}
                        {cellar_tobacco.status === 'opened' && '🟡 Opened'}
                        {cellar_tobacco.status === 'finished' && '⚪ Finished'}
                      </span>
                    </>
                  ) : tobacco ? (
                    `${tobacco.brand} • ${tobacco.type}`
                  ) : tobacco_manual ? (
                    null
                  ) : (
                    'Unknown'
                  )}
                </p>

                {/* Pipe - Priority: cellar > manual > old format */}
                {(cellar_pipe || pipe_manual || pipe_shape_used || pipe_make) && (
                  <p style={{ fontSize: '0.9rem', marginBottom: '0.5rem', color: '#d3de5e' }}>
                    🔥 {cellar_pipe ? `${cellar_pipe.maker} ${cellar_pipe.shape}${cellar_pipe.style ? ` (${cellar_pipe.style})` : ''}` : pipe_manual || (pipe_shape_used && pipe_make ? `${pipe_shape_used} - ${pipe_make}` : pipe_shape_used || pipe_make)}
                  </p>
                )}

                {/* Location */}
                {(venue || checkIn.location_name) && (
                  <p style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                    📍 {venue?.name || checkIn.location_name}
                  </p>
                )}

                {/* Amenities */}
                {amenities.length > 0 && (
                  <p style={{ fontSize: '0.85rem', marginBottom: '0.5rem', color: '#f5f5f5' }}>
                    {amenities.join(' ')}
                  </p>
                )}

                {/* Drink Pairing */}
                {drink_pairing && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9rem',
                    color: 'var(--color-accent)',
                    marginBottom: '0.5rem',
                  }}>
                    <Martini size={16} />
                    <span>Paired with: {drink_pairing}</span>
                  </div>
                )}

                {/* Lighter/Matches */}
                {(cellar_lighter || lighter_manual) && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.875rem',
                    color: '#f5f5f5',
                    marginBottom: '0.75rem',
                  }}>
                    <Flame size={14} />
                    <span>
                      {cellar_lighter ? (
                        `${cellar_lighter.brand}${cellar_lighter.style ? ` - ${cellar_lighter.style}` : ''} (${cellar_lighter.type === 'lighter' ? 'Lighter' : 'Matches'})`
                      ) : (
                        lighter_manual
                      )}
                    </span>
                  </div>
                )}

                {/* Rating (display out of 10). Existing 1-5 values are doubled visually */}
                <div style={{ display: 'flex', gap: '0.2rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                  {Array.from({ length: 10 }).map((_, i) => (
                    <Star
                      key={i}
                      size={14}
                      fill={i < ((rating <= 5 ? rating * 2 : rating) || 0) ? '#ff9500' : 'none'}
                      color={i < ((rating <= 5 ? rating * 2 : rating) || 0) ? '#ff9500' : '#6b4423'}
                    />
                  ))}
                </div>

                {/* Date, Humidity, Season */}
                <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.75rem', color: '#f5f5f5', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span>{formattedDate}</span>
                  {weather?.humidity && (
                    <span>💧 {weather.humidity}%</span>
                  )}
                  {weather?.season && (
                    <span>{weather.season}</span>
                  )}
                </div>
              </>
            )}

          </div>

          {/* Expanded Section - Notes and Actions */}
          {isExpandedDetails && (
            <div style={{ 
              marginTop: 'auto',
              paddingTop: '1rem', 
              position: 'relative',
              flex: '0 0 auto'
            }}>
          {/* Cellar Images on Left Side */}
          {(cellar_tobacco?.image_url || cellar_pipe?.image_url) && (
            <div style={{ 
              display: 'flex', 
              gap: '0.5rem', 
              justifyContent: 'flex-start',
              marginBottom: '1rem'
            }}>
              {cellar_tobacco?.image_url && (
                <div style={{ 
                  width: '60px', 
                  height: '60px', 
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid var(--color-border)'
                }}>
                  <div className="card-photo" style={{ width: '100%', height: '100%' }}>
                    <img 
                      src={cellar_tobacco.image_url} 
                      alt="Tobacco" 
                      loading="lazy"
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover' 
                      }} 
                    />
                  </div>
                </div>
              )}
              {cellar_pipe?.image_url && (
                <div style={{ 
                  width: '120px', 
                  height: '60px', 
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid var(--color-border)'
                }}>
                  <div className="card-photo" style={{ width: '100%', height: '100%' }}>
                    <img 
                      src={cellar_pipe.image_url} 
                      alt="Pipe" 
                      loading="lazy"
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'cover' 
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notes - scrollable with ellipsis indicator */}
          {checkIn.notes && (
            <div className="checkin-card-notes" ref={notesRef}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--color-accent)' }}>
                Notes:
              </h4>
              <p style={{ fontSize: '0.875rem', color: '#ffffff', lineHeight: '1.5', margin: 0 }}>
                {checkIn.notes}
              </p>
            </div>
          )}

          {/* Username - Only show for other users' check-ins if profile is public */}
          {!isOwner && (checkIn as any).profile?.username && (checkIn as any).profile?.is_profile_public !== false && (
            <div style={{ 
              marginBottom: '1rem',
              paddingTop: checkIn.notes ? '0.75rem' : '0',
              marginTop: checkIn.notes ? '0' : '0'
            }}>
              <p style={{ fontSize: '0.875rem', color: '#999', margin: 0 }}>
                by <span style={{ color: 'var(--color-accent)', fontWeight: '600' }}>@{(checkIn as any).profile.username}</span>
              </p>
            </div>
          )}

          {/* Action Buttons - Only show if user owns this check-in */}
          {isOwner && (
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Create a styled modal instead of alert
                  const modal = document.createElement('div');
                  modal.style.cssText = `
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.8);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 1rem;
                  `;
                  
                  const content = document.createElement('div');
                  content.style.cssText = `
                    background: #1a1a1a;
                    border: 1px solid var(--color-border);
                    border-radius: 12px;
                    padding: 2rem;
                    max-width: 400px;
                    width: 100%;
                    text-align: center;
                    color: #ffffff;
                  `;
                  
                  content.innerHTML = `
                    <h3 style="font-size: 1.25rem; margin-bottom: 1rem; color: var(--color-accent);">Edit Check-in</h3>
                    <p style="color: #cfcfcf; margin-bottom: 1.5rem;">Edit functionality will be available in the next update.</p>
                    <button onclick="this.closest('[style*=\"position: fixed\"]').remove()" style="
                      padding: 0.75rem 1.5rem;
                      background: var(--color-accent);
                      color: #0b0b0b;
                      border: none;
                      border-radius: 6px;
                      cursor: pointer;
                      font-weight: 600;
                    ">OK</button>
                  `;
                  
                  modal.appendChild(content);
                  document.body.appendChild(modal);
                  
                  // Auto remove after 3 seconds
                  setTimeout(() => modal.remove(), 3000);
                }}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-accent)',
                  color: 'var(--color-accent)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                }}
              >
                <Edit size={16} />
                Edit
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete();
                }}
                disabled={deleting}
                style={{
                  flex: 1,
                  padding: '0.5rem',
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-danger)',
                  color: 'var(--color-danger)',
                  borderRadius: '6px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  opacity: deleting ? 0.5 : 1,
                }}
              >
                <Trash2 size={16} />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          )}
            </div>
          )}
        </div>
      </div>

      {/* Quick nav to map (appears when expanded and coords exist, and location is shared) */}
      {isCardOpen && typeof checkIn.latitude === 'number' && typeof checkIn.longitude === 'number' && (isOwner || checkIn.is_public === true) && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/map?focusCheckin=${checkIn.id}&lat=${checkIn.latitude}&lng=${checkIn.longitude}`);
          }}
          title="View on map"
          aria-label="View on map"
          style={{
            position: 'absolute',
            top: '0.5rem',
            right: '0.5rem',
            width: '36px',
            height: '36px',
            minHeight: '36px',
            padding: 0,
            margin: 0,
            boxSizing: 'border-box',
            borderRadius: '50%',
            backgroundColor: 'var(--color-card)',
            border: '2px solid var(--color-accent)',
            color: 'var(--color-accent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5,
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
          }}
        >
          <Navigation size={18} color="var(--color-accent)" strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
}

